import { computed, inject, Signal, untracked } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { UserService } from '@core/services/user/user.service';

/**
 * Detalle que pertenece a UNA pantalla y a UN campus: el API lo acota por campus, así que
 * después de cambiar de campus estas entidades ya no existen para el usuario (404).
 *
 * `AuthService.switchCampus()` las marca stale sin refetchearlas. No se puede dejar que
 * cada pantalla se apague sola: siguen montadas (Ionic conserva la última página de cada
 * tab) y su `enabled` nuevo sólo llega al QueryObserver dentro de un effect, que corre en
 * el siguiente change detection — al cambiar de tab Ionic ni emite `ionViewWillLeave`.
 * Verificado en el app: sin esta política, cambiar de campus desde Perfil dispara
 * `bustickets/{id}` del campus anterior (404). Por eso la decisión vive en un solo lugar.
 */
const CAMPUS_SCOPED_DETAIL_KEYS = ['busticket', 'realtrip', 'route', 'live-trip'];

export function isCampusScopedDetailKey(queryKey: readonly unknown[]): boolean {
  return CAMPUS_SCOPED_DETAIL_KEYS.includes(queryKey[0] as string);
}

/**
 * Guarda para queries de detalle acotadas por campus (`bustickets/{id}`, `route/{id}`,
 * `live-trip`…). Devuelve una señal que se vuelve `false` en cuanto el campus del usuario
 * deja de ser el que tenía la pantalla al abrirse.
 *
 * Hace falta porque `AuthService.switchCampus()` invalida todo el QueryClient y las páginas
 * NO se destruyen al cambiar de campus: Ionic conserva viva la última página de cada tab, y
 * también la vista anterior del outlet raíz — verificado en el app, ni `navigateRoot` ni
 * `navigateRoot + replaceUrl` a una ruta fuera de las tabs las destruyen. Con las páginas
 * vivas, la invalidación las hace refetchear entidades que ya no existen en el campus nuevo
 * y el API responde 404 (QA lo reportó en el PR #278 con `bustickets/{id}`).
 *
 * El baseline se captura en la PRIMERA emisión con campus, no en la construcción: `user$`
 * arranca en `null` (lee storage async), y capturar en la construcción dejaría la guarda
 * abierta para siempre en páginas que montan antes de esa primera emisión.
 *
 * La guarda por sí sola no basta para el cambio de campus: cierra vía un effect que corre
 * hasta el siguiente change detection, por eso `switchCampus()` hace `appRef.tick()` antes
 * de invalidar. Aquí cubre además el polling (`refetchInterval`) y los cambios de campus
 * del lado del servidor (`getUserFromServer()`).
 *
 * Congelar la query deja la pantalla con lo último que mostró, que es justo lo que el
 * usuario ya tenía enfrente; la lista de la que cuelga sí se recarga con el campus nuevo.
 *
 * Debe llamarse en contexto de inyección (inicializador de campo del componente).
 */
export function injectCampusScopeGuard(): Signal<boolean> {
  const user = toSignal(inject(UserService).user$, { initialValue: null });
  const openedForCampus = untracked(() => user()?.campus_current ?? null);

  return computed(() => {
    const campus = user()?.campus_current ?? null;
    // Sin campus al abrir (o sin usuario ahora) no hay nada que comparar: no bloquear.
    return openedForCampus === null || campus === null || campus === openedForCampus;
  });
}
